import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ADMIN_NAV } from "@/pages/admin/nav";
import { adminService } from "@/services/adminService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, formatCurrency, formatPrice, formatQuantity, formatSignedCurrency } from "@/lib/format";
import { LocalPager, useLocalPage } from "@/components/LocalPager";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const card = "rounded-md border border-[#22252F] bg-[#12141A]";
const field = "h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 font-mono text-sm text-white";
const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

const PositionBlock = ({ block, onEdit }) => {
  const { page, pages, setPage, slice, total } = useLocalPage(block.rows, 10);
  return (
    <section className={`${card} p-6`}>
      <h2 className="font-heading text-lg font-light text-white">{block.title}</h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
            <tr>
              <th className="px-3 py-3">Ativo</th><th className="px-3 py-3">Direção</th>
              <th className="px-3 py-3 text-right">Volume</th><th className="px-3 py-3 text-right">Entrada</th>
              <th className="px-3 py-3 text-right">{block.key === "open" ? "Atual" : "Fecho"}</th>
              <th className="px-3 py-3 text-right">P/L</th>
              <th className="px-3 py-3">{block.key === "open" ? "Aberta" : "Fechada"}</th>
              {block.key === "open" ? <th className="px-3 py-3 text-right">Ação</th> : null}
            </tr>
          </thead>
          <tbody data-testid={`${block.key}-positions-body`}>
            {slice.map((p) => (
              <tr key={p.id} data-testid={`${block.key}-position-${p.id}`} className="border-b border-[#1A1D25]">
                <td className="px-3 py-3 text-white">{p.symbol}</td>
                <td className={`px-3 py-3 font-mono uppercase ${p.side === "buy" ? "text-[#10B981]" : "text-[#EF4444]"}`}>{String(p.side || "").toUpperCase()}</td>
                <td className="px-3 py-3 text-right font-mono text-[#C7D0DE]">{formatQuantity(p.quantity)}</td>
                <td className="px-3 py-3 text-right font-mono text-[#C7D0DE]">{formatPrice(p.entry_price)}</td>
                <td className="px-3 py-3 text-right font-mono text-[#C7D0DE]">{formatPrice(block.key === "open" ? p.current_price : p.close_price)}</td>
                <td className={`px-3 py-3 text-right font-mono ${p.pnl >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{formatSignedCurrency(p.pnl)}</td>
                <td className="px-3 py-3 font-mono text-xs text-[#8A97AD]">
                  {fmtDate(block.key === "open" ? p.opened_at : p.closed_at)}
                </td>
                {block.key === "open" ? (
                  <td className="px-3 py-3 text-right">
                    <button data-testid={`edit-position-${p.id}`} onClick={() => onEdit(p)}
                            className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] hover:text-white">
                      <Pencil className="mr-1 inline h-3 w-3" /> Editar posição
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
            {total === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#64748B]">Sem registos</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <LocalPager page={page} pages={pages} total={total} onPage={setPage} testId={`${block.key}-positions-pager`} />
    </section>
  );
};

export default function AdminTradingPage() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null);

  const loadClients = useCallback(async () => {
    try { setClients(await adminService.tradingClients(search)); }
    catch (e) { toast.error(errorMessage(e)); }
  }, [search]);

  useEffect(() => { if (!detail) loadClients(); }, [loadClients, detail]);

  const openClient = async (id) => {
    try { setDetail(await adminService.tradingClient(id)); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const saveAdjust = async (e) => {
    e.preventDefault();
    try {
      await adminService.adjustPosition(edit.id, {
        side: edit.side,
        entry_price: Number(edit.entry_price),
        quantity: Number(edit.quantity),
      });
      toast.success("Posição ajustada e conta recalculada");
      setEdit(null);
      openClient(detail.client.id);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (detail) {
    const a = detail.account;
    return (
      <AppShell nav={ADMIN_NAV} title={detail.client.name} subtitle="Trading do cliente">
        <div data-testid="admin-trading-detail" className="space-y-6">
          <button data-testid="back-to-trading-clients" onClick={() => setDetail(null)}
                  className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Voltar aos clientes
          </button>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { id: "account", label: "Conta", value: detail.client.account_id },
              { id: "balance", label: "Saldo", value: formatCurrency(a.balance) },
              { id: "equity", label: "Património", value: formatCurrency(a.equity) },
              { id: "margin", label: "Margem usada", value: formatCurrency(a.used_margin) },
              { id: "free", label: "Margem livre", value: formatCurrency(a.free_margin) },
              { id: "leverage", label: "Alavancagem", value: `1:${a.leverage}` },
            ].map((m) => (
              <div key={m.id} data-testid={`account-${m.id}`} className={`${card} p-4`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{m.label}</p>
                <p className="mt-2 font-mono text-base text-white">{m.value}</p>
              </div>
            ))}
          </div>

          {[
            { key: "open", title: "Operações abertas", rows: detail.open_positions },
            { key: "closed", title: "Operações fechadas (somente leitura)", rows: detail.closed_positions },
          ].map((block) => (
            <PositionBlock
              key={block.key}
              block={block}
              onEdit={(p) => setEdit({ id: p.id, side: p.side, entry_price: p.entry_price, quantity: p.quantity, symbol: p.symbol })}
            />
          ))}
        </div>

        <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="border-[#22252F] bg-[#12141A] text-white sm:max-w-md">
            <DialogHeader><DialogTitle>Ajustar posição {edit?.symbol}</DialogTitle></DialogHeader>
            <form onSubmit={saveAdjust} className="space-y-4" data-testid="adjust-position-form">
              <div className="space-y-2"><span className={labelCls}>Direção</span>
                <select data-testid="adjust-side" value={edit?.side || "buy"}
                        onChange={(e) => setEdit({ ...edit, side: e.target.value })} className={field}>
                  <option value="buy">BUY</option><option value="sell">SELL</option>
                </select></div>
              <div className="space-y-2"><span className={labelCls}>Preço de entrada</span>
                <input required data-testid="adjust-entry-price" inputMode="decimal" value={edit?.entry_price ?? ""}
                       onChange={(e) => setEdit({ ...edit, entry_price: e.target.value })} className={field} /></div>
              <div className="space-y-2"><span className={labelCls}>Volume</span>
                <input required data-testid="adjust-quantity" inputMode="decimal" value={edit?.quantity ?? ""}
                       onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} className={field} /></div>
              <Button type="submit" data-testid="submit-adjust-position"
                      className="w-full rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Guardar alteração</Button>
            </form>
          </DialogContent>
        </Dialog>
      </AppShell>
    );
  }

  return (
    <AppShell nav={ADMIN_NAV} title="Trading" subtitle="Contas, histórico e ajuste de posições">
      <div data-testid="admin-trading-page" className="space-y-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="trading-search" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Procurar cliente" className={`${field} pl-10`} />
        </div>

        <div className={`${card} overflow-x-auto`}>
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-6 py-4">Nome</th><th className="px-6 py-4">Contacto</th>
                <th className="px-6 py-4 text-right">Abertas</th><th className="px-6 py-4 text-right">Operações</th>
                <th className="px-6 py-4 text-right">Ação</th>
              </tr>
            </thead>
            <tbody data-testid="trading-clients-body">
              {clients.map((c) => (
                <tr key={c.id} data-testid={`trading-client-${c.email}`} className="border-b border-[#1A1D25] hover:bg-[#151821]">
                  <td className="px-6 py-4 text-white">{c.name}</td>
                  <td className="px-6 py-4 font-mono text-xs text-[#94A3B8]">{c.email}</td>
                  <td className="px-6 py-4 text-right font-mono text-[#C7D0DE]">{c.open_positions}</td>
                  <td className="px-6 py-4 text-right font-mono text-[#C7D0DE]">{c.trades}</td>
                  <td className="px-6 py-4 text-right">
                    <button data-testid={`view-trading-${c.email}`} onClick={() => openClient(c.id)}
                            className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] hover:text-white">Ver</button>
                  </td>
                </tr>
              ))}
              {clients.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-sm text-[#64748B]">Nenhum cliente</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
